// src/lib/sentry.ts
import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * Only runs in production environment
 */
export function initSentry(): void {
  // Only initialize in production
  if (import.meta.env.DEV) {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    // Silently skip if DSN is not configured
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // Performance Monitoring
    tracesSampleRate: 0.1, // 10% of transactions for performance monitoring

    // Session Replay (optional - comment out if not needed)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Filter out common non-actionable errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignore network errors that are expected (user offline, etc.)
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return null;
      }

      // Ignore ResizeObserver errors (browser quirk, not actionable)
      if (error instanceof Error && error.message.includes('ResizeObserver')) {
        return null;
      }

      return event;
    },

    // Ignore specific URLs
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
    ],
  });
}

/**
 * Capture an exception with Sentry
 * Safe to call even if Sentry is not initialized
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (import.meta.env.DEV) {
    console.error('[Sentry - DEV]', error, context);
    return;
  }

  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message with Sentry
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console -- dev-only diagnostics
    console.log(`[Sentry - DEV] ${level}:`, message, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  Sentry.addBreadcrumb(breadcrumb);
}
